import { useLingui } from '@lingui/react/macro';
import { createFileRoute, notFound, redirect } from '@tanstack/react-router';

import { BlogPostBySlug } from '#features/blog/components/BlogPost';
import { blogPostBySlugQueryOptions } from '#features/blog/model/blog';
import { RETIRED_BLOG_SLUGS } from '#features/blog/model/retiredSlugs';
import { ContentNotFound } from '#features/error/components/ContentNotFound';
import { Link } from '#features/routing';
import { LoadFailed } from '#features/ui';
import { envConfig } from '#constants/envConfig';
import { seo } from '#utils/seo';

export const Route = createFileRoute('/{-$locale}/_layout/blog/$slug')({
  loader: async ({ params: { slug, locale }, context }) => {
    // A renamed article: send the old address to the new one permanently, because only a
    // 301 carries its links and rankings over. The new slug is checked first on purpose:
    // the rename happens in the admin, and a deploy landing before it must not turn a live
    // URL into a 404. Costs one extra request, and only on URLs that were retired.
    const renamedTo = RETIRED_BLOG_SLUGS[slug];
    if (renamedTo) {
      const moved = await context.queryClient
        .ensureQueryData(blogPostBySlugQueryOptions(renamedTo))
        .catch(() => null);

      if (moved) {
        throw redirect({
          to: '/{-$locale}/blog/$slug',
          params: { locale, slug: renamedTo },
          statusCode: 301,
          replace: true,
        });
      }
    }

    // Only a missing record is a 404. Network and API failures remain errors, so SSR
    // keeps its service-failure status instead of telling crawlers the article was deleted.
    const data = await context.queryClient.ensureQueryData(blogPostBySlugQueryOptions(slug));
    if (!data) throw notFound();

    return {
      title: data.ogTitle,
      description: data.ogDescription,
      keywords: data.keywords,
      image: data.previewImage,
      date: data.date,
      updatedAt: data.updatedAt,
      slug,
    };
  },
  errorComponent: ArticleLoadError,
  notFoundComponent: ArticleNotFound,
  head: ({ loaderData }) => {
    if (!loaderData) return {};

    const url = `${envConfig.HOST}/blog/${loaderData.slug}`;

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: loaderData.title,
      description: loaderData.description,
      ...(loaderData.image && { image: loaderData.image }),
      ...(loaderData.date && { datePublished: loaderData.date }),
      // dateModified — из updatedAt: правка статьи должна быть видна поисковику как правка,
      // а не выглядеть публикацией, которую с тех пор не трогали.
      ...((loaderData.updatedAt ?? loaderData.date) && {
        dateModified: loaderData.updatedAt ?? loaderData.date,
      }),
      url,
      author: {
        '@type': 'Organization',
        name: 'Teamplay',
        url: envConfig.HOST,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Teamplay',
        url: envConfig.HOST,
        ...(loaderData.image && {
          logo: {
            '@type': 'ImageObject',
            url: loaderData.image,
          },
        }),
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': url,
      },
    };

    return {
      meta: seo({
        ...loaderData,
        url,
        type: 'article',
        publishedTime: loaderData.date,
        modifiedTime: loaderData.updatedAt ?? loaderData.date,
      }),
      links: [{ rel: 'canonical', href: url }],
      scripts: [
        {
          id: 'blog-post-jsonld',
          type: 'application/ld+json',
          children: JSON.stringify(jsonLd),
        },
      ],
    };
  },
  component: BlogPostBySlug,
});

function ArticleNotFound() {
  const { t } = useLingui();

  return (
    <ContentNotFound
      title={t`notFound.article.title`}
      description={t`notFound.article.description`}
      action={<Link to="/{-$locale}/blog">{t`notFound.allArticles`}</Link>}
    />
  );
}

function ArticleLoadError() {
  return (
    <LoadFailed onRetry={() => window.location.reload()} retryButtonProps={{ minH: '44px' }} />
  );
}
